import { useCallback, useEffect, useRef, useState } from 'react';

// What the server page's tabs read, kept while the page is open, so moving between tabs shows the
// last answer at once and asks the API again only when it's old. Memory only: signing in or out
// loads the page afresh, so one person's answers never reach the next.

interface Entry {
  data: unknown;
  /** When it was read (ms). */
  at: number;
  /** What it was read for: a different version (e.g. a newer backup) makes it stale at once. */
  version: string;
}

const entries = new Map<string, Entry>();

/** How long an answer is fresh enough to show without asking again. */
export const FRESH_MS = 30_000;

/** The kept answer for `key` (if any), and whether to ask again: none kept, another version, or older than `freshMs`. */
export function cached<T>(key: string, version: string, freshMs: number, now: number): { data: T | undefined; stale: boolean } {
  const entry = entries.get(key);
  if (!entry) return { data: undefined, stale: true };
  return { data: entry.data as T, stale: entry.version !== version || now - entry.at >= freshMs };
}

export function remember(key: string, version: string, data: unknown, now: number): void {
  entries.set(key, { data, at: now, version });
}

/** For tests. */
export function forgetAll(): void {
  entries.clear();
}

/**
 * Reads `key` through `read`, showing a kept answer first: asks again when it's stale (on showing,
 * and when the browser tab comes back), and whenever `reload` is called (after the person's own
 * change). Failures go to `failed`, as thrown.
 */
export function useCached<T>(
  key: string,
  read: () => Promise<T>,
  { version = '', freshMs = FRESH_MS, failed }: { version?: string; freshMs?: number; failed: (err: unknown) => void },
): { data: T | undefined; reload: () => Promise<void> } {
  const [data, setData] = useState<T | undefined>(() => cached<T>(key, version, freshMs, Date.now()).data);
  // The latest read and failure handler, so `reload` keeps one identity per key and version.
  const latest = useRef({ read, failed });
  latest.current = { read, failed };

  const reload = useCallback(async () => {
    try {
      const answer = await latest.current.read();
      remember(key, version, answer, Date.now());
      setData(answer);
    } catch (err) {
      latest.current.failed(err);
    }
  }, [key, version]);

  useEffect(() => {
    const kept = cached<T>(key, version, freshMs, Date.now());
    setData(kept.data);
    if (kept.stale) void reload();
    const back = () => {
      if (document.visibilityState === 'visible' && cached(key, version, freshMs, Date.now()).stale) void reload();
    };
    document.addEventListener('visibilitychange', back);
    return () => document.removeEventListener('visibilitychange', back);
  }, [key, version, freshMs, reload]);

  return { data, reload };
}
